import { AgileMacroplanogram } from '../types/AgileMacroplanogramTypes';
import { ZoneCreationProperties, SubcategoryAllocation } from '../types/zoneMapType';
import { FloorplanFixtureData } from '../zoneViewer/zoneMapDataType';

/**
 * Build ZoneCreationProperties for copy-to-new from existing macroplanogram data.
 * Pure function — no React state, can be called from AI service layer.
 */
export function buildCopyToNewProperties(params: {
  macroplanogramList: AgileMacroplanogram[] | null | undefined;
  agileFixtures: FloorplanFixtureData[];
  selectedDepartment: string;
  selectedProductGroup: string;
  selectedCategory: string;
  viewAllocations: SubcategoryAllocation[];
  nextGenEnabled: boolean;
  isGetFloorplanSuccess: boolean;
  productTaxonomy?: { subcategoryList?: { subcategoryId: string; categoryId: string }[] };
}): {
  zoneCreationProperties: Map<number, ZoneCreationProperties>;
  selectedFixtures: string[];
  derivedCategoryId?: string;
  derivedProductGroup?: string;
} {
  const {
    macroplanogramList,
    agileFixtures,
    selectedDepartment,
    selectedProductGroup,
    selectedCategory,
    viewAllocations,
    nextGenEnabled,
    isGetFloorplanSuccess,
    productTaxonomy,
  } = params;

  const result = new Map<number, ZoneCreationProperties>();
  result.set(0, {
    zoneIdx: 0,
    zoneName: '',
    productGroup: '',
    categoryId: '',
    subcategoryIds: [],
    subcategoryNames: [],
    fixtureIds: [],
    fixtureModels: new Map(),
    facings: 0,
    pickPaths: [],
  });
  const selectedFixtures: string[] = [];

  // GFC copy-to-new: one card per source Drop Zone, keyed off macro.categoryId.
  // Source macros have a non-blank categoryId and no storeSubcategories.
  const gfcSourceMacros = (macroplanogramList ?? []).filter(
    (macro) => !!macro.categoryId && (macro.fixtureIds?.length ?? 0) > 0,
  );
  if (gfcSourceMacros.length > 0) {
    result.clear();
    gfcSourceMacros.forEach((macro, idx) => {
      const fixtureIds = macro.fixtureIds ?? [];
      const fixtureModels = new Map<string, string>();
      if (nextGenEnabled && isGetFloorplanSuccess) {
        fixtureIds.forEach((fixtureId) => {
          const fixture = agileFixtures.find((f) => f.id === fixtureId);
          if (fixture?.model) fixtureModels.set(fixtureId, fixture.model);
        });
      }
      result.set(idx, {
        zoneIdx: idx,
        zoneName: macro.macroplanogramName ?? '',
        productGroup: selectedProductGroup || selectedDepartment || '',
        categoryId: macro.categoryId ?? '',
        subcategoryIds: [],
        subcategoryNames: [],
        fixtureIds,
        fixtureModels,
        facings: 0,
        pickPaths: [],
      });
      selectedFixtures.push(...fixtureIds);
    });
    return {
      zoneCreationProperties: result,
      selectedFixtures,
      derivedCategoryId: gfcSourceMacros[0].categoryId ?? undefined,
      derivedProductGroup: selectedProductGroup || selectedDepartment || undefined,
    };
  }

  const departmentLinkIdForCopy = selectedDepartment ? selectedDepartment.toLowerCase().trim() : null;
  const hasAllocations = macroplanogramList?.some(
    (macro) =>
      macro.storeSubcategories?.some((sc) => sc.capacityPercentage != null && sc.capacityPercentage > 0) &&
      (departmentLinkIdForCopy == null || macro.linkId === departmentLinkIdForCopy || macro.linkId == null),
  );

  if (!hasAllocations || !macroplanogramList) {
    return { zoneCreationProperties: result, selectedFixtures };
  }

  const allAllocations: SubcategoryAllocation[] = [];
  const allFixtureIds = new Set<string>();
  const seenSubcategoryIds = new Set<string>();
  const allSubcategoryIds: string[] = [];
  const allSubcategoryNames: string[] = [];
  const fixtureModels = new Map<string, string>();

  const sourceMacro = macroplanogramList.find(
    (macro) =>
      macro.storeSubcategories?.some((sc) => sc.capacityPercentage != null && sc.capacityPercentage > 0) &&
      (departmentLinkIdForCopy == null || macro.linkId === departmentLinkIdForCopy || macro.linkId == null),
  );

  if (sourceMacro) {
    (sourceMacro.fixtureIds ?? []).forEach((fId) => allFixtureIds.add(fId));
    if (nextGenEnabled && isGetFloorplanSuccess) {
      (sourceMacro.fixtureIds ?? []).forEach((fixtureId) => {
        const fixture = agileFixtures.find((f) => f.id === fixtureId);
        if (fixture?.model) fixtureModels.set(fixtureId, fixture.model);
      });
    }

    for (const viewAlloc of viewAllocations) {
      const subId = viewAlloc.subcategoryId;
      if (seenSubcategoryIds.has(subId) || viewAlloc.percentage <= 0) continue;
      seenSubcategoryIds.add(subId);
      allSubcategoryIds.push(subId);
      allSubcategoryNames.push(viewAlloc.subcategoryName);
      allAllocations.push({ ...viewAlloc });
    }
  }

  const fixtureIdsArray = Array.from(allFixtureIds);
  const derivedProductGroup = selectedProductGroup || selectedDepartment || '';

  const firstSubId = allSubcategoryIds[0];
  const matchedSubcategory = productTaxonomy?.subcategoryList?.find((s) => s.subcategoryId === firstSubId);
  const derivedCategoryId = matchedSubcategory?.categoryId || selectedCategory;

  result.set(0, {
    zoneIdx: 0,
    zoneName: allSubcategoryNames.sort().join('/'),
    productGroup: derivedProductGroup,
    categoryId: derivedCategoryId,
    subcategoryIds: allSubcategoryIds,
    subcategoryNames: allSubcategoryNames,
    fixtureIds: fixtureIdsArray,
    fixtureModels,
    facings: 0,
    pickPaths: [],
    allocations: allAllocations.length > 0 ? allAllocations : undefined,
  });
  selectedFixtures.push(...fixtureIdsArray);

  return {
    zoneCreationProperties: result,
    selectedFixtures,
    derivedCategoryId,
    derivedProductGroup,
  };
}
